import React, { useEffect } from "react";
import { Provider } from "react-redux";
import { ConnectedRouter } from "connected-react-router";
import { Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { QueryParamProvider } from "use-query-params";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import ErrorBoundary from "react-error-boundary";

import { ThemeModeProvider, useThemeMode } from "./context/ThemeModeContext";
import { FontSizeProvider, useFontSize } from "./context/FontSizeContext";
import { OrganizationThemeState } from "./context/OrganizationThemeContext";

import store, { history } from "./store";
import Routes from "./routes";
import GlobalStyles from "./components/GlobalStyles";
import GlobalStylesDarkTheme from "./components/GlobalStylesDarkTheme";
import SuccessSnackbar from "./components/common/sucessSnackbar";
import Error from "./components/errorPage";
import ChatWidget from "./chatWidget";
import SvgComponenttop from "./topsvg";
import SvgComponentbot from "./botsvg";
import theme from "./style/theme";
import darktheme from "./style/darktheme";
import "./i18n";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false },
  },
});

const trackModuleTime = () => {
  const loginTime = localStorage.getItem("loginTime");
  const stopTime = localStorage.getItem("moduleStopTracking");

  const moduleKey =
    window.location.pathname.split("/")[1] ||
    localStorage.getItem("lastActiveModule");

  if (moduleKey && (stopTime || loginTime)) {
    const duration = Date.now() - (stopTime || loginTime);
    localStorage.setItem(
      "moduleTracking",
      JSON.stringify({ [moduleKey]: duration })
    );
    localStorage.removeItem("lastActiveModule");
  }

  localStorage.setItem("moduleStopTracking", Date.now());
};

const AppContent = () => {
  const { fontSizeMultiplier } = useFontSize();
  const { themeLogin } = OrganizationThemeState();
  const { isDark } = useThemeMode();

  const baseThemeProps = {
    button_color: themeLogin?.button_color || "#009035",
    text_color: themeLogin?.text_color || "#ffffff",
    heading_color: themeLogin?.heading_color || "#68000E",
    theme_color: themeLogin?.theme_color || "#009035",
    link_color: themeLogin?.link_color || "#1295EE",
    table_head_color: themeLogin?.button_color || "#009035",
  };

  const light = createTheme({
    ...theme,
    UpdateTheme: baseThemeProps,
    typography: {
      fontSize: 14 * fontSizeMultiplier,
    },
  });

  const dark = createTheme({
    ...darktheme,
    UpdateTheme: {
      button_color: "#000",
      text_color: "#fff",
      heading_color: "#fff",
      theme_color: "#000",
      link_color: "#fff",
      table_head_color: "#000",
    },
    typography: {
      fontSize: 14 * fontSizeMultiplier,
    },
  });

  const selectedTheme = isDark ? dark : light;

  useEffect(() => {
    window.onbeforeunload = trackModuleTime;
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  return (
    <>
      {!isDark && (
        <>
          <div
            className="mui-top"
            style={{ color: baseThemeProps.theme_color }}
          >
            <SvgComponenttop height={366} width={426} />
          </div>

          <div
            className="mui-bot"
            style={{ color: baseThemeProps.theme_color }}
          >
            <SvgComponentbot />
          </div>
        </>
      )}
      <Provider store={store}>
        <ThemeProvider theme={selectedTheme}>
          <ChatWidget />
          {isDark ? <GlobalStylesDarkTheme /> : <GlobalStyles />}
          <ConnectedRouter history={history}>
            <ErrorBoundary FallbackComponent={Error}>
              <QueryParamProvider ReactRouterRoute={Route}>
                <QueryClientProvider client={queryClient}>
                  <Routes />
                  <SuccessSnackbar />
                </QueryClientProvider>
              </QueryParamProvider>
            </ErrorBoundary>
          </ConnectedRouter>
        </ThemeProvider>
      </Provider>
    </>
  );
};

export default function App() {
  return (
    <FontSizeProvider>
      <ThemeModeProvider>
        <AppContent />
      </ThemeModeProvider>
    </FontSizeProvider>
  );
}
